import QuillEditor from 'components/wysiwig/QuillEditor';
import { Controller, get, useWatch } from 'react-hook-form';
import type { Control, FieldErrors, FieldPath, FieldValues } from 'react-hook-form';

import type { JSX } from 'react';

/**
 * Quill エディタが返す「見かけ上は空」の HTML パターンを判定する。
 * 空 HTML を空文字に正規化する呼び出し元で使用する。
 */
export function isEmptyQuillHtml(value: string | null | undefined): boolean {
  if (value == null) return true;
  if (typeof value !== 'string') return true;
  const trimmed = value.trim();
  if (trimmed === '') return true;
  // Quill が空エディタで返す HTML パターン（ブラウザ / happy-dom 両対応）
  if (trimmed === '<p><br></p>') return true;
  if (trimmed === '<p></p>') return true;
  return false;
}

/**
 * HTMLエディタ入力コンポーネント
 */
export default function FormWysiwygEditor<TFieldValues extends FieldValues = FieldValues>(props: {
  // 項目名
  subject?: string;
  // 項目変数名
  name: FieldPath<TFieldValues>;
  // プレースホルダー
  placeholder?: string;
  // 必須検証の有無
  required?: boolean;
  // 必須/任意のアイコン表示有無
  requiredMark?: boolean;
  // 読み取り専用
  readOnly?: boolean;
  // フォームコントロール
  control: Control<TFieldValues>;
  // エラー情報
  errors: FieldErrors<TFieldValues>;
  // ツールバーの設定
  toolbar?: ('link' | 'button' | 'image' | 'highlight')[];
  // カスタムクラス名
  className?: string;
  // 無効化
  disabled?: boolean;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const watchedValue = useWatch({
    control: props.control,
    name: props.name,
  });

  // react-hook-form の field value（'' = 空）を Quill 内部値（'<p><br></p>'）と
  // 一致させるため、空文字を '<p><br></p>' に変換して渡す。
  const quillValue = isEmptyQuillHtml(watchedValue as string | null | undefined)
    ? '<p><br></p>'
    : (watchedValue as string);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */

  return (
    <>
      <Controller
        name={props.name}
        control={props.control}
        rules={
          props.required
            ? {
                required: '必須項目です。',
              }
            : {}
        }
        render={({ field }) => {
          return (
            <>
              {props.subject && (
                <label className="form-label">
                  {props.subject}
                  {props.requiredMark && (
                    <span className={props.required ? 'must' : 'any'}>
                      {props.required ? ' *' : ''}
                    </span>
                  )}
                </label>
              )}
              <QuillEditor
                value={quillValue}
                onChange={v => {
                  // Quill の空 HTML パターンを全て空文字に正規化して field に反映する
                  field.onChange(isEmptyQuillHtml(v) ? '' : v);
                }}
                placeholder={props.placeholder}
                readOnly={props.readOnly || props.disabled}
                toolbar={props.toolbar}
                className={`
                  ${props.className ?? ''}
                  ${props.disabled ? 'disabled' : ''}
                  ${get(props.errors, props.name) ? 'is-invalid' : ''}
                `.trim()}
              />
              {get(props.errors, props.name) && (
                <p className="error">{get(props.errors, props.name)?.message}</p>
              )}
            </>
          );
        }}
      />
    </>
  );
}
